import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ASSISTANT_COPY } from '../../constants/assistantCopy';
import composioService from '../../services/composioService';
import { describeApiError } from '../../utils/apiError';
import AssistantPage, { AssistantResult } from './AssistantPage';

/**
 * `ASSISTANT_OAUTH_REDIRECT_URL` → /assistant/oauth-done.
 *
 * Where Composio sends the browser after a sign-in that Claude / Town started
 * with `connect_toolkit`. The Integrations screen's own flow keeps using
 * /oauth/callback; this page differs only in how it ends ("go back to your
 * chat" instead of redirecting to the tools page).
 *
 * The query string goes to GET /composio/callback unchanged, with the user's
 * normal key — the route sits behind the `composio` permission. A logged-out
 * user is sent to login and back here with the query string kept.
 */
const AssistantOAuthDone = () => {
  const [searchParams] = useSearchParams();
  const [result, setResult] = useState({ tone: 'loading' });
  // The callback completes the connection server-side; StrictMode's double
  // effect must not send it twice.
  const sentRef = useRef(false);

  useEffect(() => {
    if (sentRef.current) return;
    sentRef.current = true;

    const params = Object.fromEntries(searchParams.entries());
    const { status, appName } = params;
    const providerError = params.error_description || params.error;

    if (status === 'failure' || status === 'error' || providerError) {
      setResult({
        tone: 'error',
        title: ASSISTANT_COPY.oauthFailed,
        hint: [providerError, ASSISTANT_COPY.oauthFailedHint].filter(Boolean).join(' — '),
      });
      return;
    }

    (async () => {
      try {
        const response = await composioService.handleOAuthCallback(params);
        const succeeded =
          status === 'success' ||
          response?.success ||
          response?.status === 'success' ||
          response?.connected;
        setResult(
          succeeded
            ? {
                tone: 'success',
                title: ASSISTANT_COPY.connected(appName || response?.app_name),
                hint: ASSISTANT_COPY.connectedHint,
              }
            : {
                tone: 'error',
                title: ASSISTANT_COPY.oauthFailed,
                hint: [response?.message, ASSISTANT_COPY.oauthFailedHint].filter(Boolean).join(' — '),
              }
        );
      } catch (err) {
        setResult(
          err?.status === 403
            ? { tone: 'error', title: ASSISTANT_COPY.noIntegrations, hint: ASSISTANT_COPY.noIntegrationsHint }
            : {
                tone: 'error',
                title: ASSISTANT_COPY.oauthFailed,
                hint: `${describeApiError(err, '')} ${ASSISTANT_COPY.oauthFailedHint}`.trim(),
              }
        );
      }
    })();
  }, [searchParams]);

  return (
    <AssistantPage>
      <AssistantResult
        tone={result.tone}
        title={result.tone === 'loading' ? 'Finishing the connection…' : result.title}
        hint={result.hint}
      />
    </AssistantPage>
  );
};

export default AssistantOAuthDone;
